/**
 * Turn a POST /calculate response into the same shape
 * App\Services\PriceExplanation returns for a real parcel.
 *
 * The landing calculator prices a hypothetical parcel — there is no pack to
 * ask the server about — but a visitor deserves the same answer a customer
 * gets. Rather than write a second explanation UI that could drift from the
 * first, the response is adapted into the one shape PriceExplainModal renders.
 */

const TIERS = [
   [0, 5, "0-5"],
   [5, 10, "5-10"],
   [10, 45, "10-45"],
   [45, 100, "45-100"],
   [100, Infinity, "100-99999"],
];

const tierFor = (w) => {
   const hit = TIERS.find(([min, max]) => w > min && w <= max);
   return hit ? hit[2] : "0-5";
};

export const explainFromCalculator = (res, dims) => {
   if (!res) return null;

   const real = Number(res.weight || 0);
   const volume = Number(res.volume_weight || 0);
   const chargeable = Number(res.calcWeight ?? real);
   const usedVolume = !!res.volumetric_applied;
   const minSide = Number(res.volumetric_min_side || 0);

   const sides = dims
      ? [Number(dims.x) || 0, Number(dims.y) || 0, Number(dims.z) || 0]
      : null;
   const longest = sides ? Math.max(...sides) : null;

   // Why volume didn't win, when it otherwise would have.
   let skipped = null;
   if (!usedVolume && volume > real) {
      skipped = sides && sides.some((s) => s > 0) ? "below_min_side" : "no_dimensions";
   }

   // Home-delivery offices return their bracket and a separate delivery line;
   // the tiered pipeline returns neither.
   const isHome = res.bracket !== undefined || res.delivery !== undefined;
   const base = Number(res.transport ?? res.price ?? 0);
   const delivery = Number(res.delivery || 0);

   return {
      tracking_number: null,
      shipment_code: null,
      model: isHome ? "home_delivery" : "standard",
      weight: {
         real,
         volume,
         chargeable,
         used: usedVolume ? "volume" : "real",
         dimensions: sides && sides.some((s) => s > 0)
            ? { x: sides[0], y: sides[1], z: sides[2] }
            : null,
         longest_side: longest,
         min_side_cm: minSide || null,
         volume_skipped_reason: skipped,
      },
      rate: {
         per_kg_gel: Number(res.price1kg || 0),
         // The public calculator returns the per-kg rate already in GEL and
         // doesn't expose the FX leg, so there is no foreign figure to show.
         per_kg_foreign: null,
         currency: res.currency || null,
         currency_to_gel: null,
         source: res.used_scheduled ? "scheduled" : isHome ? "home_delivery_bracket" : "group_lookup",
         bracket: isHome
            ? [res.bracket?.from_kg, res.bracket?.to_kg].filter((v) => v != null).join("-") || null
            : res.used_scheduled
            ? null
            : tierFor(chargeable),
         zone: res.zone || "tbilisi",
         city: null,
         // A quote is for one parcel, so the tier is never widened by siblings.
         group_weight: null,
         group_is_wider: false,
      },
      lines: [
         { key: "base", amount: Number(base.toFixed(2)) },
         ...(delivery ? [{ key: "delivery_overage", amount: Number(delivery.toFixed(2)) }] : []),
      ],
      total: Number(Number(res.price || 0).toFixed(2)),
      charged: Number(Number(res.price || 0).toFixed(2)),
      paid: 0,
      matches_charged: true,
      locked: false,
      lock_scope: null,
   };
};
